import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { Exact } from "@Helpers/types/exact";
import type { DatabankView } from "@ServerTypes/queries/queries.schema";
import type { FilterCondition, GroupCondition, SortCondition } from "@CubeComponents/html/SlickGrid1";

/**
 * CLIENT-SIDE state for one Databank view: only the six PERSISTED members (`DatabankView`) cross the
 * wire, the rest is grid state this slice holds. Declared here so each member names its real
 * PRODUCER's type — SlickGrid1's `FilterCondition` / `SortCondition` / `GroupCondition` — and so
 * `views` can be genuinely recursive, which the Zod `z.lazy` it replaced erased to `unknown`.
 */
export type DatabankViewData = Omit<DatabankView, "sortConditions" | "filterConditions" | "groupConditions"> & {
  /** Name of the currently-active view within this state slice. */
  selectedView?: string;
  /** Map of view name → the saved view's own state. */
  views?: Record<string, DatabankViewData>;
  /** Active per-field filter values keyed by field name (client-side grid filters). */
  filters?: Record<string, string | number | boolean | null>;
  /** Persisted sort conditions for the view, as SlickGrid1 emits them. */
  sortConditions?: SortCondition[];
  /** Per-column visibility (boolean) or width (number) settings keyed by column field name. */
  columnConfig?: Record<string, boolean | number>;
  /** Subset of `columns` (field names) currently passing the active filter/visibility rules. */
  filteredColumns?: string[];
  /** 24-hex _id of the saved query currently applied to this view. */
  queryId?: string;
  /** Active grid filters, keyed '<columnField>_<n>' → condition. */
  filterConditions?: Record<string, FilterCondition>;
  /** Active grouping entries for the grid, in order. */
  groupConditions?: GroupCondition[];
  /** Client flag: whether the saved views have been fetched/hydrated yet. */
  viewsLoaded?: boolean;
  /** Free-text search string applied over the grid rows. */
  searchQuery?: string;
  /** Number of rows per page in the grid pagination. */
  pageSize?: number;
  /** Current (1-based) page index of the grid pagination. */
  currentPage?: number;
};

/** One state slice: file-version _id → that file version's view state. */
export type DatabankFileVersionMap = Record<string, DatabankViewData>;

/** Top-level Databank state — slice id → file-version map. */
export type DatabankState = Record<string, DatabankFileVersionMap>;

/** Payload of setDatabankData / updateCurrentDatabankView — one file version's view state. */
export type SetDatabankPayload = {
  /** State-slice key (top-level Databank state id) the update targets. */
  id: string;
  /** 24-hex file-version _id whose view state within the slice is being set/merged. */
  fileVersionId: string;
  /** Partial view state to merge into the target file version's entry. */
  data: Partial<DatabankViewData>;
};

/** Payload of the resetDatabank action. */
export type ResetDatabankPayload = Omit<SetDatabankPayload, "data">;

const initialState: DatabankState = {};

const databankSlice = createSlice({
  name: "databank",
  initialState,
  reducers: {
    setDatabankData: (state: DatabankState, action: PayloadAction<SetDatabankPayload>) => {
      const { id, fileVersionId, data } = action.payload;
      state[id] ??= {};
      state[id][fileVersionId] = {
        ...(state[id][fileVersionId] ?? {}),
        ...data,
      };
    },
    updateCurrentDatabankView: (state: DatabankState, action: PayloadAction<SetDatabankPayload>) => {
      const { id, fileVersionId, data } = action.payload;
      const currentSelectedView = state[id]?.[fileVersionId]?.selectedView;
      if (!currentSelectedView) return;
      state[id] ??= {};
      const currentFileVersion = state[id][fileVersionId] ?? {};
      state[id][fileVersionId] = {
        ...currentFileVersion,
        views: {
          ...currentFileVersion.views,
          [currentSelectedView]: {
            ...(currentFileVersion.views?.[currentSelectedView] ?? {}),
            ...data,
          } as DatabankViewData,
        },
      };
    },
    resetDatabank: (state: DatabankState, action: PayloadAction<ResetDatabankPayload>) => {
      const { id, fileVersionId } = action.payload;
      state[id] ??= {};
      state[id][fileVersionId] = {};
    },
  },
});

const {
  setDatabankData: _setDatabankDataRaw,
  updateCurrentDatabankView: _updateCurrentDatabankViewRaw,
  resetDatabank: _resetDatabankRaw,
} = databankSlice.actions;

/**
 * Object-payload action creators are wrapped with `Exact<>` so excess properties
 * are caught even when the payload is passed through a variable.
 */
export const setDatabankData = <T extends Exact<SetDatabankPayload, T>>(payload: T) => _setDatabankDataRaw(payload);
export const updateCurrentDatabankView = <T extends Exact<SetDatabankPayload, T>>(payload: T) =>
  _updateCurrentDatabankViewRaw(payload);
export const resetDatabank = <T extends Exact<ResetDatabankPayload, T>>(payload: T) => _resetDatabankRaw(payload);

export const Databank = databankSlice.reducer;
export default databankSlice.reducer;
